import type { HTMLAttributes, ReactNode } from 'react'
import './surfaces.css'

/**
 * The four surface levels, plus the two special-cased panels.
 *
 * `--surface` is content, `--sunk` is table headers and nested wells,
 * `--surface-2` is reserved for the assistant and insights only (a suggestion the
 * app makes from watching the user's edit, such as Make it a rule, is an insight), `--canvas` is
 * the page itself and belongs to the layout rather than to a component.
 *
 * **No shadows.** Elevation is the hairline (`--line`) alone, and every surface
 * carries one so the whole set shares its box metrics.
 *
 * Those hairlines are **decorative**, not component-identifying: `--line` sits
 * at ~1.2:1 against its backdrop and `--accent-soft` at 1.16 light / 1.51 dark,
 * all below the 3:1 in CLAUDE.md. That is deliberate and within WCAG 1.4.11,
 * which scopes 3:1 to boundaries required to identify a component or its state
 * — each panel is identified by its fill, and the hairline only softens the
 * edge. An earlier version of this comment argued the opposite, that without a
 * boundary a low-contrast panel would be unidentifiable; that cannot be right
 * while shipping 1.16:1, since a boundary nobody can see rescues nothing. If
 * the boundary is ever genuinely load-bearing, `--accent-soft` is the wrong
 * token and it wants `--line-2` or a new one.
 *
 * **The skins are in surfaces.css** (#116). This file decides which surface a
 * component IS; the stylesheet decides what that looks like.
 */

type Props = HTMLAttributes<HTMLDivElement> & { children: ReactNode }

/**
 * The shared box metrics — padding, radius, flex column, reserved border.
 *
 * Separate from the identity classes below because they are separate concerns:
 * `.sunk-well` says what a surface is PAINTED with and what context it hands
 * down; `.surface-box` says what BOX it is. #31's DataGrid wants the first
 * without the second, and welding them together left it no way to say so.
 *
 * Named `surface-box`, not `surface`. In CLAUDE.md "surface" means a FILL —
 * `--surface`, `--sunk`, `--surface-2`, `--canvas` — and putting that word on
 * the one class carrying no fill, four lines above
 * `.panel { background: var(--surface) }`, teaches the opposite of what this
 * split exists to teach. `<div className="surface">` would get an unpainted
 * flex box.
 */
export const SURFACE_BOX = 'surface-box'

/**
 * The class that identifies each surface.
 *
 * It does two jobs, and they are worth keeping distinct because they do not
 * cover the same set:
 *
 *   - **the skin**, in surfaces.css. All five have one.
 *   - **the control context**, in tokens.css (#98) — the `--control-*` triple a
 *     surface hands to the controls inside it. Only the four OPAQUE surfaces
 *     have one. `AlertPanel` deliberately does not: `--neg-soft` is
 *     translucent, so it composites over whatever is beneath it and must
 *     inherit that surface's context rather than assert a backdrop it does not
 *     establish.
 *
 * Exported so a consumer imports the string rather than retyping it — #31's
 * `DataGrid` wants a sunk header row, and a typo in `'sunk-well'` there would
 * fail silently, giving the header the page's context instead of the well's.
 * Strings, not colours, so this is not the JS theme object CLAUDE.md prohibits.
 */
export const SURFACE_CLASSES = {
  panel: 'panel',
  heroTile: 'hero-tile',
  sunkWell: 'sunk-well',
  assistantPanel: 'assistant-panel',
  alertPanel: 'alert-panel',
} as const

const CLASSES: readonly string[] = [SURFACE_BOX, ...Object.values(SURFACE_CLASSES)]

/**
 * `displayName` is set per surface. The factory alone gave all five the
 * function name `Surface`, which is what a stack trace and the DevTools tree
 * would then show for every one of them.
 */
function make(name: string, surfaceClass: string) {
  const Surface = ({ children, style, className, ...rest }: Props) => {
    /**
     * A caller's className cannot introduce a SECOND surface class.
     *
     * `<HeroTile className="panel">` used to yield `class="hero-tile panel"`,
     * and because both rules are (0,1,0) the winner was decided by source order
     * — `.panel` — giving a control on a lime tile `--ink`, at 1.11:1 in dark.
     * web-tester measured it. Now that the fills are in the same cascade, the
     * same collision would take the fill with it, so this matters more than it
     * did and not less.
     */
    const caller = (className ?? '')
      .split(/\s+/)
      .filter((c) => c && !CLASSES.includes(c))
      .join(' ')
    return (
      // `style` is the caller's deliberate override and still beats the
      // stylesheet, exactly as it beat the skin object before. The difference
      // is that it is now the ONLY inline declaration — see surfaces.css.
      <div
        className={[SURFACE_BOX, surfaceClass, caller].filter(Boolean).join(' ')}
        style={style}
        {...rest}
      >
        {children}
      </div>
    )
  }
  Object.defineProperty(Surface, 'name', { value: name })
  Surface.displayName = name
  return Surface
}

export const Panel = make('Panel', SURFACE_CLASSES.panel)

/**
 * Controls inside this tile sit on lime, and `--accent` is the SAME lime in
 * both themes while `--ink` flips to near-white — so a default button's label
 * measures 1.12:1 here in dark, and an outline button's default boundary
 * 1.40 light / 1.01 dark while that was `--line-2` — now `--ink2`, which is no
 * better on lime at 5.45 light / 1.06 dark, so the override below is still what
 * rescues dark. tokens.css keys off `.hero-tile` to hand down `--on-accent` /
 * `--on-accent-2`, which hold in both themes.
 */
export const HeroTile = make('HeroTile', SURFACE_CLASSES.heroTile)

export const SunkWell = make('SunkWell', SURFACE_CLASSES.sunkWell)

export const AssistantPanel = make('AssistantPanel', SURFACE_CLASSES.assistantPanel)

export const AlertPanel = make('AlertPanel', SURFACE_CLASSES.alertPanel)
